import {
  MeshBasicMaterial,
  Mesh,
  DoubleSide,
  AlwaysStencilFunc,
  ReplaceStencilOp,
  EqualStencilFunc,
  KeepStencilOp,
  Object3D,
  PlaneGeometry,
  Color,
} from "three";
import { VBase } from "./VBase";
import VUtil from "../VUtils";
import { VGridLayout, StartAxis } from "../UILayout/VLayout";
import { VSlider2 } from "./_VSlider2";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";

const defaultOptions = {
  color: 0x676767,
  opacity: 1,
  sliderColor: 0xffffff,
  handleColor: 0x00ffff,
  sliderOpacity: 1,
  handleOpacity: 1,
  sliderHeight: 0,
  map: null,
  gridLayout: new VGridLayout(),
  width: 1,
  height: 1,
  radius: 0.05,
  showSlider: true,
  sliderExitState: "transparent",
  disabledColor: 0xc8c8c8,
};

/*
 使用案例
    // 添加元素的函数
      let addMatItem = (scrollview, data) => {
        const container = this.uiLib.createBtn({
          text: "",
          opacity: 0.5,
          color: 0x4f5670,
          width: 0.22,
          height: 0.25,
          radius: 0.02,
        });
        // 添加空函数覆盖默认的放大事件
        container.addHoverEnterEvent(() => {});
        container.addHoverExitEvent(() => {});
        container.addClickEvent((obj) => {
          console.log("选中对象", obj);
        });
        // 为UI对象添加滚动视图的光标进入事件
        container._scrollviewData = data;

        let str = data.name;
        if (!str) str = "";
        if (str.length > 7) {
          str = str.substring(0, 7) + "...";
        }

        const name = this.uiLib.createText({
          text: str,
          textColor: 0xffffff,
          textSize: 0.025,
          align: "left",
          createdCB: (tex) => {
            // 文字对象创建在后面 为UI对象添加滚动视图的光标进入事件
            scrollview.setSomethingForLater(name);
          },
        });
        name.position.x = -0.09;
        name.position.y = -0.09;
        name.position.z = 0.001;
        container.add(name);

        scrollview.addContent(container);
      };

      const layout = new VGridLayout();
      layout.setCellSize(new Vector2(0.3, 0.32));
      layout.setCountLimit(4);
      layout.setSpacing(new Vector2(0.02, 0.02));

      const scrollview = this.uiLib.createScrollViewGrid({
        renderOrder: 99999,
        opacity: 0.5,
        width: 1.0,
        height: 0.5,
        radius: 0.05,
        gridLayout: layout,
        sliderColor: 0xafbbe3,
        handleColor: 0x8993c3,
        sliderOpacity: 0.5,
        handleOpacity: 0.8,
        sliderExitState: "hide",
        sliderHeight: 0.02,
      });
      // 注册光标进入元素事件
      scrollview.addOnElementHoverEnterEvent((item) => {
        console.log("光标进入元素：", item);
      });
      // 注册光标离开元素事件
      scrollview.addOnElementHoverExitEvent((item) => {
        console.log("光标离开元素：", item);
      });
      // 注册滚动区域垂直滚动事件
      scrollview.addOnVerticleSliderValueChangeEvent((v) => {
        console.log("滚动区域发生垂直滚动：" + v);
      });
      // 注册滚动区域水平滚动事件
      scrollview.addOnHorizontalSliderValueChangeEvent((v) => {
        console.log("滚动区域发生水平滚动：" + v);
      });
      this.uiLib.addUIToScene(scrollview);

      const datas = [
        {
          _id: "678a1b7fab9c0a26bca8757e",
          name: "Material-5",
          path: "public/texture/678a1b6cab9c0a26bca87573/Material-5.json",
        },
        {
          _id: "678a1b7fab9c0a26bca8757c",
          name: "Material-4",
          path: "public/texture/678a1b6cab9c0a26bca87573/Material-4.json",
        },
        {
          _id: "678a1b7fab9c0a26bca8757a",
          name: "Material-3",
          path: "public/texture/678a1b6cab9c0a26bca87573/Material-3.json",
        },
        {
          _id: "678a1b7fab9c0a26bca8757",
          name: "Material-5",
          path: "public/texture/678a1b6cab9c0a26bca87573/Material-5.json",
        },
        {
          _id: "678a1b7fab9c0a26bca8757",
          name: "Material-4",
          path: "public/texture/678a1b6cab9c0a26bca87573/Material-4.json",
        },
        {
          _id: "678a1b7fab9c0a26bca8757",
          name: "Material-3",
          path: "public/texture/678a1b6cab9c0a26bca87573/Material-3.json",
        },
      ];
      for (var i = 0; i < datas.length; i++) {
        addMatItem(scrollview, datas[i]);
      }

      // 移除指定索引的元素
      scrollview.removeContentByIndex(1, 1);
 */

export class VScrollViewGrid extends VBase {
  /**
   * 网格滚动视图，多行多列，放置固定大小的对象，对于宽度高度不对的会自动缩放为设置的宽度高度
   * options：{
   *    priority: 优先级，默认为1，值越小越靠前
   *    renderOrder: 渲染顺序，默认为99989
   *    bgColor：滚动视图背景色，默认为0x676767
   *    opacity: 滚动视图背景透明度，默认为1，不透明
   *    sliderColor: 滑动条背景色，默认为0xfffff
   *    handleColor: 滑动条handle颜色，默认为0x00ffff
   *    sliderOpacity: 滑动条透明度，默认为1，不透明
   *    handleOpacity: 滑动条handle透明度，默认为1，不透明
   *    sliderHeight：滑动条高度，设置为0将由滚动视图宽度决定，默认为0
   *    map：滚动视图背景图片，默认为null，不是地址
   *    gridLayout：网格布局参数，是 VHorizontalLayout 类型对象，控制元素的排列和间隙
   *    width：滚动视图宽度，默认为1
   *    height：滚动视图高度，默认为1
   *    radius: 圆角半径，设为0则是矩形，默认为0.05
   *    showSlider: 是否显示滑动条，设置为false将永远不会显示滑动条，默认为true
   *    sliderExitState: 当光标离开滚动视图后滑动条的状态显示。"normal"-没有变化  "hide"-隐藏  "transparent"-半透明，默认为"transparent"
   *    disabledColor: ui对象禁用颜色，默认为 0xC8C8C8
   * }
   * @param {*} options
   */
  constructor(options, vuiLib) {
    if (!options) options = {};

    const tempOptions = {
      priority: options.priority !== undefined ? options.priority : 1,
      renderOrder:
        options.renderOrder !== undefined ? options.renderOrder : 99989,
      color: options.color !== undefined ? options.color : defaultOptions.color,
      opacity:
        options.opacity !== undefined
          ? options.opacity
          : defaultOptions.opacity,
      map: options.map ? options.map : defaultOptions.map,
      width: options.width !== undefined ? options.width : defaultOptions.width,
      height:
        options.height !== undefined ? options.height : defaultOptions.height,
      radius:
        options.radius !== undefined ? options.radius : defaultOptions.radius,
      disabledColor:
        options.disabledColor !== undefined
          ? options.disabledColor
          : defaultOptions.disabledColor,
      sliderColor:
        options.sliderColor !== undefined
          ? options.sliderColor
          : defaultOptions.sliderColor,
      handleColor:
        options.handleColor !== undefined
          ? options.handleColor
          : defaultOptions.handleColor,
      sliderOpacity:
        options.sliderOpacity !== undefined
          ? options.sliderOpacity
          : defaultOptions.sliderOpacity,
      handleOpacity:
        options.handleOpacity !== undefined
          ? options.handleOpacity
          : defaultOptions.handleOpacity,
      sliderHeight:
        options.sliderHeight !== undefined
          ? options.sliderHeight
          : defaultOptions.sliderHeight,
      gridLayout: options.gridLayout
        ? options.gridLayout
        : defaultOptions.gridLayout,
      showSlider:
        options.showSlider !== undefined
          ? options.showSlider
          : defaultOptions.showSlider,
      sliderExitState: options.sliderExitState
        ? options.sliderExitState
        : defaultOptions.sliderExitState,
    };

    super(tempOptions, vuiLib);
    this._vType = "VScrollViewGrid";

    this._otherProps = {
      sliderSpeed: 0.02, // 通过按键滑动视图时的速度
      transpanrentSliderOpacity: 0.35, // 半透明状态的滑动条透明度
      sliderColor: tempOptions.sliderColor,
      handleColor: tempOptions.handleColor,
      sliderOpacity: tempOptions.sliderOpacity,
      handleOpacity: tempOptions.handleOpacity,
      sliderHeight: tempOptions.sliderHeight,
      gridLayout: tempOptions.gridLayout,
      showSlider: tempOptions.showSlider,
      sliderExitState: tempOptions.sliderExitState,
      onHorizontalSliderValueChange: [],
      onVerticleSliderValueChange: [],
      onElementHoverEnter: [],
      onElementHoverExit: [],
    };

    this.stencilRef = this.vuilib.getStencil();
    this.uiContents = []; // 存储添加到滚动区域的UI对象
    this.needAddUIs = []; // 准备添加到滚动区域的UI对象
    this.needRemoveUIs = [];
    this.needRemoveUIIndexs = [];
    this.interval = null;

    this.name = "VScrollViewGrid";

    // 遮罩区域
    {
      this.viewWidth =
        this._baseProps.width - this._otherProps.gridLayout.border.x * 2;
      if (this.viewWidth <= 0) {
        console.warn("滚动视图可视区域错误，请检查horizontalLayout参数");
        this.viewWidth = this._baseProps.width;
      }
      this.viewHeight =
        this._baseProps.height - this._otherProps.gridLayout.border.y * 2;
      if (this.viewHeight <= 0) {
        console.warn("滚动视图可视区域错误，请检查horizontalLayout参数");
        this.viewHeight = this._baseProps.height;
      }
      let geo = VUtil.createRectangleGeometry(
        this.viewWidth,
        this.viewHeight,
        this._baseProps.radius
      );
      let maskMat = new MeshBasicMaterial({
        color: 0xff0000,
        side: DoubleSide,
        stencilWrite: true, // 启用模板写入
        stencilRef: this.stencilRef, // 模板参考值
        stencilFunc: AlwaysStencilFunc, // 总是通过模板测试
        stencilZPass: ReplaceStencilOp, // 通过深度测试时，将模板值替换为参考值
        transparent: true,
        opacity: 0.01, //0.01,
        depthTest: false,
        depthWrite: false,
        colorWrite: false,
        stencilFuncMask: 0xff, // 使用全8位掩码
        stencilFail: ReplaceStencilOp, // 新增
        stencilZFail: ReplaceStencilOp, // 新增
      });
      VUtil.convertSRGBToLinear(maskMat);

      this.viewArea = new Mesh(geo, maskMat);
      this.viewArea.renderOrder = this._baseProps.renderOrder;
      this.add(this.viewArea);
    }

    // 内容区域
    this.content = new Object3D();
    this.viewArea.add(this.content);
    this.content.position.x = -this.viewWidth / 2;
    this.content.position.y = this.viewHeight / 2;
    this.contentWidth = 0; // 当前滚动视图内容总宽度
    this.contentHeight = 0; // 当前滚动视图内容总高度

    // 滑动条
    {
      let sliderHeight = 0;
      if (this._otherProps.sliderHeight !== 0) {
        sliderHeight = this._otherProps.sliderHeight;
      } else {
        sliderHeight = 0.05;
      }

      // 水平滑动条
      {
        this.horizontalSlider = new VSlider2({
          color: this._otherProps.sliderColor,
          handleColor: this._otherProps.handleColor,
          width: this._baseProps.width,
          height: sliderHeight,
          radius: sliderHeight / 2,
          opacity: this._otherProps.sliderOpacity,
          handleOpacity: this._otherProps.sliderOpacity,
        });
        this.horizontalSlider.setProgressValue(0);

        if (!this._otherProps.showSlider) {
          this.horizontalSlider.material.visible = false;
          this.horizontalSlider.handle.material.visible = false;
          this.horizontalSlider._dontCheckEcsy = true;
          this.horizontalSlider.handle._dontCheckEcsy = true;
        }

        this.add(this.horizontalSlider);
        this.horizontalSlider.position.y =
          -this._baseProps.height / 2 + sliderHeight / 2;
        this.horizontalSlider.position.z += 0.004;

        this.horizontalSlider.visible = false;
        this.horizontalSlider.handle.visible = false;
        this.horizontalSlider.material.transparent = true;
        this.horizontalSlider.handle.material.transparent = true;
        if (this._otherProps.sliderExitState === "transparent") {
          this.horizontalSlider.material.opacity =
            this._otherProps.transpanrentSliderOpacity;
          this.horizontalSlider.handle.material.opacity =
            this._otherProps.transpanrentSliderOpacity;
        }

        this.horizontalSlider.addOnValueChangeEvent((v) => {
          this._horizontalSliderValueChanged(v);
        });

        this.horizontalSlider.addHoverEnterEvent(() => {
          this.onHoverEnter();
        });
        this.horizontalSlider.addHoverExitEvent(() => {
          this.onHoverExit();
        });
        this.horizontalSlider.addHandlerHoverEnterEvent(() => {
          this.onHoverEnter();
        });
        this.horizontalSlider.addHandlerHoverExitEvent(() => {
          this.onHoverExit();
        });
      }
      // 垂直滑动条
      {
        this.verticleSlider = new VSlider2({
          color: this._otherProps.sliderColor,
          handleColor: this._otherProps.handleColor,
          width: this._baseProps.height,
          height: sliderHeight,
          handleOpacity: this._otherProps.handleOpacity,
          opacity: this._otherProps.sliderOpacity,
          radius: sliderHeight / 2,
        });
        this.verticleSlider.setProgressValue(0);
        this.verticleSlider.rotateZ(-Math.PI / 2);

        if (!this._otherProps.showSlider) {
          this.verticleSlider.material.visible = false;
          this.verticleSlider.handle.material.visible = false;
          this.verticleSlider._dontCheckEcsy = true;
          this.verticleSlider.handle._dontCheckEcsy = true;
        }

        this.add(this.verticleSlider);
        this.verticleSlider.position.x =
          this._baseProps.width / 2 - sliderHeight / 2;
        this.verticleSlider.position.z += 0.005;

        this.verticleSlider.visible = false;
        this.verticleSlider.material.transparent = true;
        this.verticleSlider.handle.material.transparent = true;
        if (this._otherProps.sliderExitState === "transparent") {
          this.verticleSlider.material.opacity =
            this._otherProps.transpanrentSliderOpacity;
          this.verticleSlider.handle.material.opacity =
            this._otherProps.transpanrentSliderOpacity;
        }

        this.verticleSlider.addOnValueChangeEvent((v) => {
          this._verticleSliderValueChanged(v);
        });

        this.verticleSlider.addHoverEnterEvent(() => {
          this.onHoverEnter();
        });
        this.verticleSlider.addHoverExitEvent(() => {
          this.onHoverExit();
        });
        this.verticleSlider.addHandlerHoverEnterEvent(() => {
          this.onHoverEnter();
        });
        this.verticleSlider.addHandlerHoverExitEvent(() => {
          this.onHoverExit();
        });
      }
    }

    if (this.vuilib) {
      this.vuilib.addComponents(
        this.horizontalSlider.handle,
        this._priority - 1
      );
      this.vuilib.addComponents(this.verticleSlider.handle, this._priority - 1);
    }
  }

  clear() {
    this.horizontalSlider.clear();
    this.verticleSlider.clear();
    this._clearBorder();

    super.clear();
  }

  /**
   * 水平滑动条变化时的回调
   * @param {*} v
   */
  _horizontalSliderValueChanged(v) {
    if (this.contentWidth > this.viewWidth) {
      this.content.position.x =
        -this.viewWidth / 2 - v * (this.contentWidth - this.viewWidth);

      for (
        var i = 0;
        i < this._otherProps.onHorizontalSliderValueChange.length;
        i++
      ) {
        this._otherProps.onHorizontalSliderValueChange[i](v);
      }
    }
  }

  /**
   * 设置水平滑动条的值
   * @param {*} v
   * @returns
   */
  setHorizontalSliderValue(v) {
    if (!this._baseProps.enabled) return;
    this.horizontalSlider.setProgressValue(v);
  }

  /**
   * 垂直滑动条变化时的回调
   * @param {*} v
   */
  _verticleSliderValueChanged(v) {
    if (this.contentHeight > this.viewHeight) {
      this.content.position.y =
        this.viewHeight / 2 + v * (this.contentHeight - this.viewHeight);

      for (
        var i = 0;
        i < this._otherProps.onVerticleSliderValueChange.length;
        i++
      ) {
        this._otherProps.onVerticleSliderValueChange[i](v);
      }
    }
  }

  /**
   * 设置垂直滑动条数值
   * @param {*} v
   * @returns
   */
  setVerticleSliderValue(v) {
    if (!this._baseProps.enabled) return;
    this.verticleSlider.setProgressValue(v);
  }

  /**
   * 添加UI对象到滚动视图
   * 目标是支持所有的UI类型（VButton、VText、VContainer、VLider）且支持滚动视图嵌套，深层次交互。
   * 暂时不支持深层次交互
   * @param {Mesh} uiContent 由VUILib创建的ui对象
   * @param {Int} indexX 水平方向索引，添加到指定索引位置上。默认为-1，添加到最后一列
   * @param {Int} indexY 垂直方向索引，添加到指定索引位置上。默认为-1，添加到最后一行
   */
  addContent(uiContent, indexX = -1, indexY = -1) {
    // 垂直优先时交换索引
    if (this._otherProps.gridLayout.startAxis === StartAxis.Verticle) {
      let v = indexX;
      indexX = indexY;
      indexY = v;
    }

    // 文字内容可能暂时还没有获取到宽度和高度，添加到数组中循环检测等待获取到宽度
    if (
      (uiContent._vType === "VText" && uiContent.height === 0) ||
      this.needAddUIs.length > 0
    ) {
      uiContent._scrollViewIndexX = indexX;
      uiContent._scrollViewIndexY = indexY;
      this.needAddUIs.push(uiContent);
      if (!this.interval) {
        this.interval = setInterval(() => {
          this._checkNeedAddContent();
        }, 50);
      }
    } else {
      this._addContent(uiContent, indexX, indexY);
    }
  }

  /**
   * 从滚动视图移除一个ui对象
   * @param {*} uiContent
   */
  removeContent(uiContent) {
    if (this.interval !== null) {
      this.needRemoveUIs.push(uiContent);
    } else {
      this._removeContent(uiContent);
    }
  }

  /**
   * 移除滚动视图内所有UI对象
   */
  clearContent() {
    if (this.interval !== null) {
      for (var i = 0; i < this.uiContents.length; i++) {
        this.needRemoveUIs.push(this.uiContents[i]);
      }
    } else {
      while (this.uiContents.length > 0) {
        this._removeContent(this.uiContents[0]);
      }
    }
  }

  /**
   * 根据索引从滚动视图移除一个ui对象
   * @param {*} indexX 水平方向索引
   * @param {*} indexY 垂直方向索引
   */
  removeContentByIndex(indexX, indexY) {
    if (indexX <= -1 || indexY <= -1) {
      console.warn("VScrollViewGrid error: 索引错误，移除对象失败");
      return;
    }

    // 垂直优先时交换索引
    if (this._otherProps.gridLayout.startAxis === StartAxis.Verticle) {
      let v = indexX;
      indexX = indexY;
      indexY = v;
    }

    if (this.interval !== null) {
      this.needRemoveUIIndexs.push({ indexX, indexY });
    } else {
      this._removeContentByIndex(indexX, indexY);
    }
  }

  /**
   * 用于循环检测是否还有待添加到滚动视图的对象
   */
  _checkNeedAddContent() {
    if (this.needAddUIs.length > 0) {
      if (this.needAddUIs[0].width !== 0 || this.needAddUIs[0].height !== 0) {
        let indexX = this.needAddUIs[0]._scrollViewIndexX;
        let indexY = this.needAddUIs[0]._scrollViewIndexY;
        delete this.needAddUIs[0]._scrollViewIndexX;
        delete this.needAddUIs[0]._scrollViewIndexY;
        this._addContent(this.needAddUIs[0], indexX, indexY);
        this.needAddUIs.splice(0, 1);
      }
    }

    if (this.needAddUIs.length <= 0) {
      clearInterval(this.interval);
      this.interval = null;

      if (this.needRemoveUIs.length > 0) {
        for (var i = 0; i < this.needRemoveUIs.length; i++) {
          this._removeContent(this.needRemoveUIs[i]);
        }
        this.needRemoveUIs = [];
      }
      if (this.needRemoveUIIndexs.length > 0) {
        for (var j = 0; j < this.needRemoveUIIndexs.length; j++) {
          this._removeContentByIndex(
            this.needRemoveUIIndexs[j].indexX,
            this.needRemoveUIIndexs[j].indexY
          );
        }
        this.needRemoveUIIndexs = [];
      }
    }
  }

  /**
   * 为UI对象添加滚动视图的光标进入事件，让UI对象也可以光标进入时显示滑动条
   * @param {*} uiContent
   */
  _addHoverEnterEventForContent(uiContent) {
    if (uiContent._vType) {
      uiContent.addHoverEnterEvent(() => {
        this.onHoverEnter();
      });
    }
  }

  /**
   * 为UI对象添加滚动视图的光标离开事件，让UI对象也可以光标离开时隐藏滑动条
   * @param {*} uiContent
   */
  _addHoverExitEventForContent(uiContent) {
    if (uiContent._vType) {
      uiContent.addHoverExitEvent(() => {
        this.onHoverExit();
      });
    }
  }

  /**
   * 为UI对象添加检测是否可选中，在滚动视图中，对于区域外的ui（被模板测试剔除了，无法看见）不能被选中
   * @param {*} uiContent
   */
  _addCheckSelectableEventForContent(uiContent) {
    if (uiContent._vType) {
      uiContent.addCheckSelectableEvent((worldPos) => {
        return this.checkSelectable(worldPos);
      });
    }
  }

  /**
   * 设置材质的模板值，如果添加的元素内部有VText类型，则需要在添加到滚动视图后再设置
   * 包括后面对元素进行修改后也需要重新设置
   * @param {*} mat
   */
  _setMatStancil(mat) {
    const mats = Array.isArray(mat) ? mat : [mat];
    for (var i = 0; i < mats.length; i++) {
      mats[i].stencilWrite = true;
      mats[i].stencilRef = this.stencilRef;
      mats[i].stencilFunc = EqualStencilFunc;
      mats[i].stencilZFail = KeepStencilOp;
      mats[i].transparent = true;
      mats[i].stencilFuncMask = 0xff; // 使用全8位掩码
      //mats[i].stencilFail = KeepStencilOp; // 新增 高版本（171）不能加这个
    }
  }

  /**
   * 为添加到滚动视图才后创建的ui子对象添加一些设置（主要针对文字）
   * @param {*} uiContent
   */
  setSomethingForLater(uiContent) {
    uiContent.traverse((node) => {
      node.renderOrder += this._baseProps.renderOrder;
      if (node.material) {
        this._setMatStancil(node.material);
      }
    });
    this._addCheckSelectableEventForContent(uiContent);
    this._addHoverEnterEventForContent(uiContent);
    this._addHoverExitEventForContent(uiContent);
  }

  /**
   * 内部使用
   * @param {*} uiContent
   * @param {*} index
   */
  _addContent(uiContent, indexX = -1, indexY = -1) {
    // 设置事件
    uiContent.addHoverEnterEvent((target) => {
      this._onElementHoverEnter(target);
    });
    uiContent.addHoverExitEvent((target) => {
      this._onElementHoverExit(target);
    });
    uiContent.addCheckSelectableEvent((worldPos) => {
      return this.checkSelectable(worldPos);
    });

    // 设置ui对象的模板
    uiContent.traverse((node) => {
      node.renderOrder += this._baseProps.renderOrder;
      if (node.material) {
        this._setMatStancil(node.material);
      }
    });

    // 进行缩放
    if (uiContent.width !== this._otherProps.gridLayout.cellSize.x) {
      let r = this._otherProps.gridLayout.cellSize.x / uiContent.width;
      uiContent.scale.x *= r;
      uiContent.width = this._otherProps.gridLayout.cellSize.x;
    }
    // 进行缩放
    if (uiContent.height !== this._otherProps.gridLayout.cellSize.y) {
      let r = this._otherProps.gridLayout.cellSize.y / uiContent.height;
      uiContent.scale.y *= r;
      uiContent.height = this._otherProps.gridLayout.cellSize.y;
    }

    // 第一个元素
    if (this.uiContents.length === 0) {
      this.uiContents.push(uiContent);
      this.content.add(uiContent);
    }
    // 两个索引都为-1，直接添加在最后面
    else if (indexX <= -1 && indexY <= -1) {
      this.uiContents.push(uiContent);
      this.content.add(uiContent);
    }
    // 第一个索引不为-1，添加在指定行或列的最后面
    else if (indexX > -1 && indexY <= -1) {
      let srcCount = this.uiContents.length;
      const count1 = Math.floor(
        srcCount / this._otherProps.gridLayout.countLimit
      );
      const count2 = srcCount % this._otherProps.gridLayout.countLimit;

      // 索引错误,直接放最后面
      if (count2 === 0 && indexX > count1 - 1) {
        this.uiContents.push(uiContent);
        this.content.add(uiContent);
      }
      // 插入最后一行也是直接放最后面
      else if (count2 !== 0 && indexX === count1) {
        this.uiContents.push(uiContent);
        this.content.add(uiContent);
      } else {
        this.uiContents.splice(
          (count1 + 1) * this._otherProps.gridLayout.countLimit - 1,
          0,
          uiContent
        );
        this.content.add(uiContent);
      }
    }
    // 第二个索引不为-1，添加在最后一行或最后一列的指定位置
    else if (indexX <= -1 && indexY > -1) {
      let srcCount = this.uiContents.length;
      const count1 = Math.floor(
        srcCount / this._otherProps.gridLayout.countLimit
      );
      const count2 = srcCount % this._otherProps.gridLayout.countLimit;

      // 需要另开一行或一列，相当于直接添加到末尾
      if (count2 === 0) {
        this.uiContents.push(uiContent);
        this.content.add(uiContent);
      }
      // 超出范围，添加在最后面
      else if (indexY >= count2) {
        this.uiContents.push(uiContent);
        this.content.add(uiContent);
      } else {
        this.uiContents.splice(
          count1 * this._otherProps.gridLayout.countLimit + indexY,
          0,
          uiContent
        );
        this.content.add(uiContent);
      }
    }
    // 两个索引都不为-1
    else {
      let srcCount = this.uiContents.length;
      const count1 = Math.floor(
        srcCount / this._otherProps.gridLayout.countLimit
      );
      const count2 = srcCount % this._otherProps.gridLayout.countLimit;

      // 索引1超出范围，添加在最后面
      if (indexX > count1 + 1) {
        this.uiContents.push(uiContent);
        this.content.add(uiContent);
      } else {
        // 在最后一行或最后一列添加
        if (indexX === count1 + 1) {
          if (indexY >= count2) {
            this.uiContents.push(uiContent);
            this.content.add(uiContent);
          } else {
            this.uiContents.splice(
              count1 * this._otherProps.gridLayout.countLimit + indexY,
              0,
              uiContent
            );
            this.content.add(uiContent);
          }
        } else {
          if (indexY >= this._otherProps.gridLayout.countLimit)
            indexY = this._otherProps.gridLayout.countLimit;

          this.uiContents.splice(
            indexX * this._otherProps.gridLayout.countLimit + indexY,
            0,
            uiContent
          );
          this.content.add(uiContent);
        }
      }
    }

    uiContent.position.z += 0.0001;

    this._updateContentPosition();
  }

  /**
   * 更新元素的位置
   */
  _updateContentPosition() {
    if (this.uiContents.length > 0) {
      switch (this._otherProps.gridLayout.startAxis) {
        case StartAxis.Horizontal: {
          for (var i = 0; i < this.uiContents.length; i++) {
            let count1 = Math.floor(i / this._otherProps.gridLayout.countLimit);
            let count2 = i % this._otherProps.gridLayout.countLimit;

            this.uiContents[i].position.x =
              count2 *
                (this._otherProps.gridLayout.cellSize.x +
                  this._otherProps.gridLayout.spacing.x) +
              this.uiContents[i].width / 2;

            this.uiContents[i].position.y =
              -count1 *
                (this._otherProps.gridLayout.cellSize.y +
                  this._otherProps.gridLayout.spacing.y) -
              this.uiContents[i].height / 2;
          }
          break;
        }
        case StartAxis.Verticle: {
          for (var j = 0; j < this.uiContents.length; j++) {
            let count1 = Math.floor(j / this._otherProps.gridLayout.countLimit);
            let count2 = j % this._otherProps.gridLayout.countLimit;

            this.uiContents[j].position.x =
              count1 *
                (this._otherProps.gridLayout.cellSize.x +
                  this._otherProps.gridLayout.spacing.x) +
              this.uiContents[j].width / 2;

            this.uiContents[j].position.y =
              -count2 *
                (this._otherProps.gridLayout.cellSize.y +
                  this._otherProps.gridLayout.spacing.y) -
              this.uiContents[j].height / 2;
          }
          break;
        }
        default: {
          break;
        }
      }
    }

    // 计算content总宽度和高度
    {
      let count = this.uiContents.length;
      const count1 = Math.ceil(count / this._otherProps.gridLayout.countLimit);
      const count2 = count % this._otherProps.gridLayout.countLimit;
      // 没有元素
      if (count1 === 0) {
        this.contentHeight = 0;
        this.contentWidth = 0;
      } else {
        switch (this._otherProps.gridLayout.startAxis) {
          case StartAxis.Horizontal: {
            this.contentHeight =
              count1 * this._otherProps.gridLayout.cellSize.y +
              (count1 - 1) * this._otherProps.gridLayout.spacing.y;

            // 只有一行但这行未满
            if (
              count1 === 1 &&
              count2 < this._otherProps.gridLayout.countLimit &&
              count2 !== 0
            ) {
              this.contentWidth =
                count2 * this._otherProps.gridLayout.cellSize.x +
                (count2 - 1) * this._otherProps.gridLayout.spacing.x;
            }
            // 有多行元素
            else {
              this.contentWidth =
                this._otherProps.gridLayout.countLimit *
                  this._otherProps.gridLayout.cellSize.x +
                (this._otherProps.gridLayout.countLimit - 1) *
                  this._otherProps.gridLayout.spacing.x;
            }

            break;
          }
          case StartAxis.Verticle: {
            this.contentWidth =
              count1 * this._otherProps.gridLayout.cellSize.x +
              (count1 - 1) * this._otherProps.gridLayout.spacing.x;

            // 只有一列但这列未满
            if (
              count1 === 1 &&
              count2 < this._otherProps.gridLayout.countLimit &&
              count2 !== 0
            ) {
              this.contentHeight =
                count2 * this._otherProps.gridLayout.cellSize.y +
                (count2 - 1) * this._otherProps.gridLayout.spacing.y;
            }
            // 有多列元素
            else {
              this.contentHeight =
                this._otherProps.gridLayout.countLimit *
                  this._otherProps.gridLayout.cellSize.y +
                (this._otherProps.gridLayout.countLimit - 1) *
                  this._otherProps.gridLayout.spacing.y;
            }
            break;
          }
          default: {
            break;
          }
        }
      }
    }

    // 更新滑动条显示隐藏
    if (this.hoverEntering) {
      if (this.contentWidth > this.viewWidth) {
        this.horizontalSlider.visible = true;
      } else {
        this.horizontalSlider.visible = false;
      }
      if (this.contentHeight > this.viewHeight) {
        this.verticleSlider.visible = true;
      } else {
        this.verticleSlider.visible = false;
      }
    }
  }

  /**
   * 内部使用
   * @param {*} uiContent
   */
  _removeContent(uiContent) {
    this.vuilib.removeUI(uiContent);
    let index = this.uiContents.indexOf(uiContent);
    if (index >= 0) {
      this.uiContents.splice(index, 1);
      uiContent.clear();

      this._updateContentPosition();

      if (this.border && this.curHoverElement) {
        this.border.position.copy(this.curHoverElement.position);
        this.border.position.z += 0.001;
      }
    }
  }

  /**
   * 内部使用
   * @param {*} index
   */
  _removeContentByIndex(indexX, indexY) {
    const index = indexX * this._otherProps.gridLayout.countLimit + indexY;
    if (index >= this.uiContents.length) {
      console.warn("索引超出范围，移除失败");
      return;
    }

    if (index >= 0 && index < this.uiContents.length) {
      this.removeContent(this.uiContents[index]);
    }
  }

  _onLeftBtnEvent(value) {
    if (!this._baseProps.enabled) return;
    if (this.horizontalSlider.value <= 0) return;

    const speed = Math.abs(value) * this._otherProps.sliderSpeed;

    if (this.horizontalSlider.value - speed >= 0) {
      this.horizontalSlider.setProgressValue(
        this.horizontalSlider.value - speed
      );
    } else {
      this.horizontalSlider.setProgressValue(0);
    }
  }

  _onRightBtnEvent(value) {
    if (!this._baseProps.enabled) return;
    if (this.horizontalSlider.value >= 1) return;

    const speed = Math.abs(value) * this._otherProps.sliderSpeed;

    if (this.horizontalSlider.value + speed <= 1) {
      this.horizontalSlider.setProgressValue(
        this.horizontalSlider.value + speed
      );
    } else {
      this.horizontalSlider.setProgressValue(1);
    }
  }

  _onUpBtnEvent(value) {
    if (!this._baseProps.enabled) return;
    if (this.verticleSlider.value >= 1) return;

    const speed = Math.abs(value) * this._otherProps.sliderSpeed;

    if (this.verticleSlider.value + speed <= 1) {
      this.verticleSlider.setProgressValue(this.verticleSlider.value + speed);
    } else {
      this.verticleSlider.setProgressValue(1);
    }
  }

  _onDownBtnEvent(value) {
    if (!this._baseProps.enabled) return;
    if (this.verticleSlider.value <= 0) return;

    const speed = Math.abs(value) * this._otherProps.sliderSpeed;

    if (this.verticleSlider.value - speed >= 0) {
      this.verticleSlider.setProgressValue(this.verticleSlider.value - speed);
    } else {
      this.verticleSlider.setProgressValue(0);
    }
  }

  /**
   * 光标进入滚动视图
   */
  onHoverEnter() {
    if (!this._baseProps.enabled) return;
    this.vuilib.onUpBtnEvent = (value) => {
      this._onUpBtnEvent(value);
    };
    this.vuilib.onDownBtnEvent = (value) => {
      this._onDownBtnEvent(value);
    };
    this.vuilib.onLeftBtnEvent = (value) => {
      this._onLeftBtnEvent(value);
    };
    this.vuilib.onRightBtnEvent = (value) => {
      this._onRightBtnEvent(value);
    };

    // 设置滑动条显示隐藏
    switch (this._otherProps.sliderExitState) {
      case "normal": {
        break;
      }
      case "hide": {
        if (this.contentWidth > this.viewWidth) {
          this.horizontalSlider.visible = true;
        }
        if (this.contentHeight > this.viewHeight) {
          this.verticleSlider.visible = true;
        }
        break;
      }
      case "transparent": {
        this.horizontalSlider.material.opacity = 1;
        this.horizontalSlider.handle.material.opacity = 1;

        this.verticleSlider.material.opacity = 1;
        this.verticleSlider.handle.material.opacity = 1;
        break;
      }
      default: {
        break;
      }
    }

    this.hoverEntering = true;

    super.onHoverEnter();
  }

  /**
   * 光标离开滚动视图
   */
  onHoverExit() {
    this.hoverEntering = false;
    //if (!this._baseProps || !this._baseProps.enabled) return;
    this.vuilib.onUpBtnEvent = null;
    this.vuilib.onDownBtnEvent = null;
    this.vuilib.onLeftBtnEvent = null;
    this.vuilib.onRightBtnEvent = null;

    if (this._otherProps) {
      // 设置滑动条显示隐藏
      switch (this._otherProps.sliderExitState) {
        case "normal": {
          break;
        }
        case "hide": {
          this.horizontalSlider.visible = false;
          this.verticleSlider.visible = false;
          break;
        }
        case "transparent": {
          this.horizontalSlider.material.opacity =
            this._otherProps.transpanrentSliderOpacity;
          this.horizontalSlider.handle.material.opacity =
            this._otherProps.transpanrentSliderOpacity;

          this.verticleSlider.material.opacity =
            this._otherProps.transpanrentSliderOpacity;
          this.verticleSlider.handle.material.opacity =
            this._otherProps.transpanrentSliderOpacity;
          break;
        }
        default: {
          break;
        }
      }
    }

    super.onHoverExit();
  }

  _onElementHoverEnter(target) {
    if (!this._baseProps.enabled) return;

    this.curHoverElement = target;

    if (this._otherProps.onElementHoverEnter.length > 0) {
      for (var i = 0; i < this._otherProps.onElementHoverEnter.length; i++)
        this._otherProps.onElementHoverEnter[i](target);
    } else {
      if (!this.border) {
        this.border = this._createBorder(
          this._otherProps.gridLayout.cellSize.x,
          this._otherProps.gridLayout.cellSize.y
        );
        this.content.add(this.border);
      }
      this.border.visible = true;

      this.border.position.copy(target.position);
      this.border.position.z += 0.001;
    }

    this.onHoverEnter();
  }

  _onElementHoverExit(target) {
    if (!this._baseProps.enabled) return;
    //console.log(target);

    this.curHoverElement = null;

    if (this._otherProps.onElementHoverExit.length > 0) {
      for (var i = 0; i < this._otherProps.onElementHoverExit.length; i++)
        this._otherProps.onElementHoverExit[i](target);
    }
    if (this.border) {
      this.border.visible = false;
    }

    this.onHoverExit();
  }

  getClickable() {
    if (!this.visible || !this._baseProps.enabled) return [];
    const res = [];
    if (this._otherProps.showSlider) {
      if (this.horizontalSlider.visible) {
        res.push(this.horizontalSlider.handle);
      }
      if (this.verticleSlider.visible) {
        res.push(this.verticleSlider.handle);
      }
    }

    return res;
  }

  /**
   * 重写基类的检测是否可选中事件
   * @param {*} worldPos
   * @returns
   */
  checkSelectable(worldPos) {
    if (!this._baseProps.enabled) return false;

    const localPos = this.worldToLocal(worldPos);

    if (isNaN(localPos.x) || isNaN(localPos.y)) return false;

    if (
      Math.abs(localPos.y) > this.viewHeight / 2 ||
      Math.abs(localPos.x) > this.viewWidth / 2
    ) {
      return false;
    }
    return true;
  }

  _createBorder(width, height) {
    this._clearBorder();
    let points = [
      -width / 2,
      height / 2,
      0,

      width / 2,
      height / 2,
      0,

      width / 2,
      -height / 2,
      0,

      -width / 2,
      -height / 2,
      0,

      -width / 2,
      height / 2,
      0,
    ];

    let color = new Color(0xff6d33);
    let colors = [
      color.r,
      color.g,
      color.b,

      color.r,
      color.g,
      color.b,

      color.r,
      color.g,
      color.b,

      color.r,
      color.g,
      color.b,

      color.r,
      color.g,
      color.b,
    ];

    const geometry = new LineGeometry();
    geometry.setPositions(points);
    geometry.setColors(colors);
    const matLine = new LineMaterial({
      color: 0xffffff,
      linewidth: 0.002, // in world units with size attenuation, pixels otherwise
      vertexColors: true,
      dashed: false,
      alphaToCoverage: true,
      transparent: true,

      stencilWrite: true, // 启用模板写入
      stencilRef: this.stencilRef, // 模板参考值
      stencilFunc: EqualStencilFunc, // 只渲染模板值等于参考值的区域
      stencilZFail: KeepStencilOp, // 深度测试失败时保持模板值
      stencilFuncMask: 0xff, // 使用全8位掩码
      stencilFail: KeepStencilOp, // 新增
    });

    const line = new Line2(geometry, matLine);
    //line.computeLineDistances();
    line.renderOrder = this._baseProps.renderOrder + 1;
    return line;
  }

  _clearBorder() {
    if (this.border) {
      this.border.geometry.dispose();
      this.border.material.dispose();
      this.border.removeFromParent();
      this.border = null;
    }
  }

  /**
   * 增加一个 光标进入滚动视图元素事件回调，回调参数为当前进入的元素对象
   * @param {*} value
   */
  addOnElementHoverEnterEvent(fun) {
    if (!fun) return;
    this._otherProps.onElementHoverEnter.push(fun);
  }

  /**
   * 移除一个 光标进入滚动视图元素事件回调
   * @param {*} fun
   */
  removeOnElementHoverEnterEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onElementHoverEnter.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onElementHoverEnter.splice(index, 1);
    }
  }

  /**
   * 移除所有 光标进入滚动视图元素事件回调
   */
  removeAllOnElementHoverEnterEvent() {
    this._otherProps.onElementHoverEnter = [];
  }

  /**
   * 增加一个 光标离开滚动视图元素事件回调，回调参数为当前离开的元素对象
   * @param {*} fun
   */
  addOnElementHoverExitEvent(fun) {
    if (!fun) return;
    this._otherProps.onElementHoverExit.push(fun);
  }

  /**
   * 移除一个 光标离开滚动视图元素事件回调
   * @param {*} fun
   */
  removeOnElementHoverExitEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onElementHoverExit.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onElementHoverExit.splice(index, 1);
    }
  }

  /**
   * 移除所有 光标离开滚动视图元素事件回调
   */
  removeAllOnElementHoverExitEvent() {
    this._otherProps.onElementHoverExit = [];
  }

  /**
   * 添加水平滑动条值改变回调事件，回调参数为当前滑动条的值
   * @param {*} fun
   * @returns
   */
  addOnHorizontalSliderValueChangeEvent(fun) {
    if (!fun) return;
    this._otherProps.onHorizontalSliderValueChange.push(fun);
  }

  /**
   * 移除一个 水平滑动条值改变回调事件
   * @param {*} fun
   * @returns
   */
  removeOnHorizontalSliderValueChangeEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onHorizontalSliderValueChange.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onHorizontalSliderValueChange.splice(index, 1);
    }
  }

  /**
   * 移除所有 水平滑动条值改变回调事件
   * @returns
   */
  removeAllOnHorizontalSliderValueChangeEvent() {
    this._otherProps.onHorizontalSliderValueChange = [];
  }

  /**
   * 添加垂直滑动条值改变回调事件，回调参数为当前滑动条的值
   * @param {*} fun
   * @returns
   */
  addOnVerticleSliderValueChangeEvent(fun) {
    if (!fun) return;
    this._otherProps.onVerticleSliderValueChange.push(fun);
  }

  /**
   * 移除一个 垂直滑动条值改变回调事件
   * @param {*} fun
   * @returns
   */
  removeOnVerticleSliderValueChangeEvent(fun) {
    if (!fun) return;
    let index = this._otherProps.onVerticleSliderValueChange.indexOf(fun);
    if (index >= 0) {
      this._otherProps.onVerticleSliderValueChange.splice(index, 1);
    }
  }

  /**
   * 移除所有 垂直滑动条值改变回调事件
   * @returns
   */
  removeAllOnVerticleSliderValueChangeEvent() {
    this._otherProps.onVerticleSliderValueChange = [];
  }
}
